import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../../base/i18n/functions';
import { isInterpreter } from '../../../base/user-role/functions';
import { queueService } from '../../InterpreterQueueService';
import { DEFAULT_INTERPRETER_LANGUAGES } from '../../interpreterLanguages';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { prepareInterpreterOfferAlerts } from '../../interpreterOfferAlert.web';
import { setPauseAfterCall } from '../../actions';
import { getPersistentJson } from '../../../vrs-auth/storage';
const useStyles = makeStyles()(theme => ({
    docked: { color: theme.palette.text01, fontSize: 14, lineHeight: '22px' },
    dockedActions: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2), marginTop: theme.spacing(2) },
    pauseAfterCall: {
        alignItems: 'center', cursor: 'pointer', display: 'flex', fontWeight: 600, gap: theme.spacing(2),
        marginTop: theme.spacing(2), minHeight: 48,
        '& input': { accentColor: theme.palette.action01, height: 20, margin: 0, width: 20 }
    },
    // Overlay, not layout: floats translucent over the video and only takes
    // full opacity on hover so it never competes with the call itself.
    overlayRoot: {
        position: 'fixed',
        top: '16px',
        right: '16px',
        zIndex: 1000,
        opacity: 0.35,
        transition: 'opacity 0.25s ease',
        '&:hover, &:focus-within': {
            opacity: 1
        }
    },
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: 'rgba(26, 35, 50, 0.8)',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    statusIndicator: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        transition: 'all 0.3s ease'
    },
    statusOnline: {
        backgroundColor: '#28a745',
        boxShadow: '0 0 8px rgba(40, 167, 69, 0.6)'
    },
    statusOffline: {
        backgroundColor: '#6c757d'
    },
    statusBusy: {
        backgroundColor: '#dc3545',
        boxShadow: '0 0 8px rgba(220, 53, 69, 0.6)'
    },
    label: {
        fontSize: '13px',
        color: 'white',
        fontWeight: 500
    },
    toggle: {
        position: 'relative',
        width: '44px',
        height: '24px',
        backgroundColor: '#6c757d',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    toggleActive: {
        backgroundColor: '#28a745'
    },
    toggleKnob: {
        position: 'absolute',
        top: '2px',
        left: '2px',
        width: '20px',
        height: '20px',
        backgroundColor: 'white',
        borderRadius: '50%',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
    },
    toggleKnobActive: {
        transform: 'translateX(20px)'
    },
    busyToggle: {
        position: 'relative',
        width: '44px',
        height: '24px',
        backgroundColor: '#6c757d',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    busyToggleActive: {
        backgroundColor: '#dc3545'
    },
    dropdown: {
        position: 'relative'
    },
    menu: {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '8px',
        backgroundColor: '#1a2332',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        padding: '8px 0',
        minWidth: '200px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
        zIndex: 1000
    },
    menuItem: {
        padding: '10px 16px',
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        transition: 'background 0.2s',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
    },
    menuItemSelected: {
        backgroundColor: 'rgba(40, 167, 69, 0.2)'
    },
    menuIcon: {
        fontSize: '16px'
    }
}));
// Display-only seed: what the interpreter last chose, so a fresh conference
// page load does not claim Offline mid-queue. 'busy' is never seeded or sent —
// it is derived below while the interpreter is on a call.
function seedStatusFromPersistedChoice() {
    if (queueService.getLastInterpreterStatus() === 'active') {
        return 'available';
    }
    try {
        return window.localStorage.getItem('vrs_interpreter_available') === 'true' ? 'available' : 'offline';
    }
    catch (e) {
        return 'offline';
    }
}
/**
 * Interpreter status toggle component.
 *
 * Two real states the interpreter chooses between — Available and Paused
 * (shown as "offline" in code) — plus a third display state, On a call, which is
 * derived from being matched and is not itself a choice: busy and offline mean
 * the same thing to the queue (not matching), so offering both as menu options
 * implied a distinction the server cannot honor.
 */
const InterpreterStatusToggle = ({ _inCall: matchedInCall, _isConnected, _pauseAfterCall, _room, dispatch, t, docked = false }) => {
    const { classes } = useStyles();
    // The web interpreter reaches the room with a full page load, so this
    // page's queue state often has no match: the stored active call for this
    // room is what says a call is under way.
    const storedCallInRoom = useMemo(() => {
        const stored = getPersistentJson('vrs_active_call');
        return Boolean(_room && stored?.roomName === _room);
    }, [_room]);
    const _inCall = matchedInCall || storedCallInRoom;
    const [status, setStatus] = useState(seedStatusFromPersistedChoice);
    const [showMenu, setShowMenu] = useState(false);
    // Being matched into a call is authoritative over any manual choice; when
    // the call ends, return to whatever was chosen before it.
    useEffect(() => {
        if (_inCall) {
            setStatus('busy');
        }
        else {
            setStatus(seedStatusFromPersistedChoice());
        }
    }, [_inCall]);
    const handleStatusChange = (newStatus) => {
        setStatus(newStatus);
        setShowMenu(false);
        // Persist the choice so the dashboard restores it after the call —
        // hanging up used to silently drop the interpreter out of the queue.
        try {
            window.localStorage.setItem('vrs_interpreter_available', String(newStatus === 'available'));
        }
        catch (e) {
            // Storage unavailable — the live socket update below still applies.
        }
        if (newStatus === 'available') {
            void prepareInterpreterOfferAlerts();
            queueService.updateInterpreterStatus('active', undefined, DEFAULT_INTERPRETER_LANGUAGES);
        }
        else {
            queueService.updateInterpreterStatus('inactive');
        }
    };
    // Don't render if not an interpreter
    if (!isInterpreter()) {
        return null;
    }
    const statusConfig = {
        offline: { icon: '⚫', label: t('vrs.statusOffline'), class: classes.statusOffline },
        available: { icon: '🟢', label: t('vrs.statusAvailable'), class: classes.statusOnline },
        busy: { icon: '🔴', label: t('vrs.statusBusy'), class: classes.statusBusy }
    };
    const currentConfig = statusConfig[status];
    if (docked) {
        return React.createElement("div", { className: classes.docked },
            React.createElement("div", { role: 'status' }, _isConnected ? currentConfig.label : 'Matching disconnected'),
            _inCall ? React.createElement(React.Fragment, null,
                React.createElement("label", { className: classes.pauseAfterCall },
                    React.createElement("input", { checked: _pauseAfterCall, onChange: event => dispatch(setPauseAfterCall(event.target.checked)), role: 'switch', type: 'checkbox' }),
                    React.createElement("span", null, "Pause after this call"))) : React.createElement("div", { className: classes.dockedActions },
                React.createElement(Button, { disabled: !_isConnected || status === 'available', label: 'Go available', onClick: () => handleStatusChange('available'), type: BUTTON_TYPES.TERTIARY }),
                React.createElement(Button, { disabled: !_isConnected || status === 'offline', label: 'Pause', onClick: () => handleStatusChange('offline'), type: BUTTON_TYPES.TERTIARY })));
    }
    return (React.createElement("div", { className: `${classes.overlayRoot} ${classes.dropdown}` },
        React.createElement("div", { className: classes.container, onClick: () => setShowMenu(!showMenu), style: { cursor: 'pointer' } },
            React.createElement("div", { className: `${classes.statusIndicator} ${currentConfig.class}` }),
            React.createElement("span", { className: classes.label }, currentConfig.label),
            React.createElement("span", { style: { fontSize: '10px', opacity: 0.7 } }, "\u25BC")),
        showMenu && (React.createElement("div", { className: classes.menu, onClick: (e) => e.stopPropagation() },
            React.createElement("div", { className: `${classes.menuItem} ${status === 'available' ? classes.menuItemSelected : ''}`, onClick: () => handleStatusChange('available') },
                React.createElement("span", { className: classes.menuIcon }, "\uD83D\uDFE2"),
                React.createElement("span", null, t('vrs.statusAvailable'))),
            React.createElement("div", { className: `${classes.menuItem} ${status === 'offline' ? classes.menuItemSelected : ''}`, onClick: () => handleStatusChange('offline') },
                React.createElement("span", { className: classes.menuIcon }, "\u26AB"),
                React.createElement("span", null, t('vrs.statusOffline')))))));
};
function _mapStateToProps(state) {
    const queueState = state['features/interpreter-queue'];
    return {
        _inCall: Boolean(queueState?.matchFound || queueState?.matchData),
        _isConnected: queueState?.isConnected || false,
        _pauseAfterCall: Boolean(queueState?.pauseAfterCall),
        _room: state['features/base/conference']?.room
    };
}
export default translate(connect(_mapStateToProps)(InterpreterStatusToggle));
